import * as THREE from 'three';
import { Vue, Component } from "vue-property-decorator";
import OrbitControls from 'orbit-controls-es6';
const cloneDeep = require("lodash/cloneDeep");

@Component
export class external3DScene extends Vue {
  data() {
    return {
      model3d: null,
      renderer: null,
      scene: null,
      camera: null,
      showRotateMsg: false,
      flippedUGCTexture: null,
    }
  };

  get isMobile() {
    return this.$store.getters["utils/returnScreenSize"].includes('mobile');
  }

  get pdm() {
    return this.$store.state.utils.pdm;
  };

  get isApparel() {
    return this.$store.getters['utils/isApparelEnabled'];
  }

  get customsAssets() {
    return this.$store.state.endPoints.customizer.customsAssets;
  }

  get s3ModelsPath() {
    return `${this.$store.state.endPoints.customizer.s3ModelsBucket}gltf_models/${this.pdm.sku}/${this.$store.state.sceneSetup.defaultSize}/`;
  }

  async initExternal3Dscene({ domEl, sizeDivider=1, snapshotSize="", snapshotView="", showSnapShot=false, embroidery=false, embroideryIndex=0, applyUGC=false}) {
    const { features, activeUGCIndex } = this.$store.state.ugc.tempData;
    const { responses, activeUGCComponent } = features[activeUGCIndex];
    const { modelBySize, defaultSize, loadedModelName } = this.$store.state.sceneSetup;
    const pdmView = snapshotSize && this.pdm.product_images?.snapshot_views?.[snapshotView];
    const pdmRendition = snapshotView && this.pdm.product_images?.renditions?.[snapshotSize];
    const el = document.getElementById(domEl);
    const zoomValue = !this.isApparel ? 600 : 1400;

    // copy scene
    this.scene = this.$model3D.sceneHelpers.getScene().clone();

    const size = !!modelBySize ? modelBySize : defaultSize;
    // set up scene
    this.model3d = this.scene.getObjectByName(`${this.pdm.parent_model || this.pdm.sku}-${size}`);
    this.rootObj = this.scene.getObjectByName("rootObj");
    this.adjustScaleMobile(snapshotSize,snapshotView);
    this.model3d.traverse(child => {
      if (child.isMesh) {
        child.material = child.material;
      }
    });
    // get renderer data
    const { antialias, high_resolution } = this.pdm.default_display.scene_defaults.renderer;
    const { precision, shadowMap, shadowMapSoft, shadowMapType } = high_resolution;
    const preserveDrawingBuffer = this.$store.getters["utils/getUrlParameterByName"]("preserveDrawingBuffer") === "false";
    const DPR = snapshotSize ? 1 : window.devicePixelRatio || 1;
    let { innerWidth: WW, innerHeight: HH } = window;
    if(snapshotSize) {
      WW = pdmRendition.width;
      HH = pdmRendition.height;
    }
    snapshotSize && this.model3d.position.set(pdmView.position.x,pdmView.position.y,pdmView.position.z);
    let height = !this.isMobile || snapshotSize ? HH : WW;
    // dispose main renderer (ThreeJS should only have one)
    this.$model3D.renderer.dispose();
    // create renderer
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias, precision, preserveDrawingBuffer });
    // get camera data
    const { camera_settings } = this.pdm.default_display.scene_defaults
    const pdmControls = this.pdm.default_display.scene_defaults.controls.desktop;
    // create new camera
    this.camera = new THREE.PerspectiveCamera(
      camera_settings.fov,
      WW / height,
      camera_settings.near,
      camera_settings.far
    );
    this.camera.position.set(camera_settings.position[0], camera_settings.position[1], camera_settings.position[2]);
    this.camera.setLens(camera_settings.setLens);
    this.camera.zoom = snapshotSize ? pdmView.zoom : !this.isMobile ? (window.innerHeight / zoomValue).toFixed(1) : 2.2;
    this.camera.aspect = WW / height;
    this.camera.updateProjectionMatrix();
    // set up orbit controls
    const controls = new OrbitControls(this.camera, this.renderer.domElement);
    controls.enableDamping = pdmControls.enableDamping;
    controls.dampingFactor = 0.25;
    controls.enableZoom = false;
    controls.maxPolarAngle = pdmControls.maxPolarAngle > 0 ? pdmControls.maxPolarAngle : Math.PI / 2;
    controls.minPolarAngle = pdmControls.minPolarAngle > 0 ? pdmControls.minPolarAngle : 0;
    controls.zoomSpeed = pdmControls.zoomSpeed;
    controls.enableRotate = true;
    controls.rotateSpeed = pdmControls.rotateSpeed;
    controls.enableKeys = pdmControls.enableKeys;
    controls.enablePan = pdmControls.enablePan;
    controls.autoRotateSpeed = pdmControls.autoRotateSpeed;
    controls.autoRotate = false; // Set false so as not to conflict w/ intro animation
    controls.update();

    // init renderer
    if(snapshotSize && snapshotView) {
      this.renderer.domElement.style.display="none";
      this.renderer.setSize(WW, HH, true);
      pdmView.camera_position && this.camera.position.set(pdmView.camera_position.x,pdmView.camera_position.y,pdmView.camera_position.z);
      pdmView.camera_position && this.camera.updateProjectionMatrix();
      pdmView.scale && this.model3d.scale.set(pdmView.scale.x,pdmView.scale.y,pdmView.scale.z);
      pdmView.floor && await this.handleFloor(pdmView);
      pdmView.rotation && this.model3d.rotation.set(pdmView.rotation.x,pdmView.rotation.y,pdmView.rotation.z);
      embroidery && await new Promise((resolve, reject) => {
        const leftShoe = (embroideryIndex === 0);
        const componentMesh = this.model3d.getObjectByName("personalization");
        this.model3d.remove(componentMesh);
        this.$personalization.applyTextToCanvas({ personalizationComponent: this.$store.getters["personalization/getPersonalizationComponentName"], leftShoe, snapShotModel: this.model3d }, resolve);
      })
      applyUGC && await this.handleUGC(false);
      pdmView.scale.y < 0 && await this.handleLeftShoe(pdmView);
      controls.update();
    }
    else if (this.isMobile && !this.isApparel) {
      this.renderer.setSize(WW, height);
    }
    else {
      this.renderer.setSize(WW / sizeDivider, HH / sizeDivider);
    }

    if(snapshotSize && !pdmView.transparent)
      this.renderer.setClearColor(Number(pdmRendition.background_color), 1);
    else {
      this.renderer.setClearColor(0x000000, 0);
    }

    this.renderer.setPixelRatio(DPR);
    this.renderer.shadowMap.enabled = shadowMap;
    this.renderer.shadowMapSoft = shadowMapSoft;
    this.renderer.shadowMap.type = THREE[shadowMapType];
    el?.appendChild(this.renderer.domElement);

    this.showRotateMsg = true;
    // animate
    this.animateExternal3dScene();
    if(!snapshotSize) {
      controls.addEventListener('change', this.animateExternal3dScene );
    }

    if(snapshotSize && snapshotView) { 
      return await this.screenShot(snapshotSize, snapshotView, showSnapShot, !!pdmView.floor);
    }

    setTimeout(() => {
      this.showRotateMsg = false;
    }, 3000);
  };

  animateExternal3dScene() {
    this.renderer.render(this.scene, this.camera);
  };

  adjustScaleMobile(snapshotSize, snapshotView) {
    if(!this.isMobile) return;
    const { responsive_defaults } = this.pdm.default_display.scene_defaults;
    let matchedDefaults = {};
    if(snapshotSize && snapshotView) {
      matchedDefaults = this.pdm.default_display.scene_defaults.responsive_defaults.find(r => r.breakpoint === 1024 )
    } else {
      for (let item of responsive_defaults) {
        let query = "only screen";
        query = `${query} and (min-width: ${item.breakpoint}px)`;
        if (typeof(item.orientation) !== 'undefined') {
          query = `${query} and (orientation: ${item.orientation})`;
        }
        if(window.matchMedia(query).matches) {
          matchedDefaults = item;
        }
      }
    }
    this.rootObj.scale.set(
      matchedDefaults.scale.x,
      matchedDefaults.scale.y,
      matchedDefaults.scale.z
    );
  }

  applyTexture({imgUrl, componentName}) {
    const componentPDM = this.pdm.components[componentName];
    const merchandisingUGC = componentPDM.ugc;
    const material_options = { ...merchandisingUGC.ugc_swatch.material_options };
    const textureList = JSON.parse(
      JSON.stringify(material_options.textures)
    );
    textureList.map.image_url = `${this.$store.state.endPoints.customizer.s3Url}${imgUrl}`;

    textureList.normalMap.image_url = `${this.$store.getters['endPoints/swatchesPath']()}${textureList.normalMap.image_url}`

    return this.$model3D.loadMultipleTextures(textureList)
      .then(loadedTextures => {
        const component = this.model3d.getObjectByName(componentName);
        material_options.mesh_options.color = new THREE.Color(this.$store.state.ugc.template_guide_bg);

        component.material = this.$model3D.sceneHelpers.createMaterial({
          mesh_type: material_options.type,
          options: Object.assign({}, material_options.mesh_options, loadedTextures),
          componentName,
          shadersType: componentPDM.ugc.shader_material
        });
      });
  };

  async handleFloor(pdmView) {
    const floor = this.model3d.getObjectByName('floor');
    if(pdmView.floor.visible === false) {
      floor.visible = false;
    } else {
      const floorMaterial = floor.material.clone();
      const color = new THREE.Color('#000');
      const image_url = `${this.s3ModelsPath}floor_pass/${pdmView.floor.image_url}`
      const texture = await this.$model3D.sceneHelpers.loadTexture({path: image_url});
      floorMaterial.map = null;
      floorMaterial.alphaMap = texture;
      floorMaterial.color = color;
      floor.material = floorMaterial;
      floor.material.needsUpdate = true;
    }
  }

  async handleUGC(leftShoe) {
    const recipe = this.$store.getters["ugc/tempData/getRecipe"];
    const recipe_v2 = this.$store.state.recipe.recipe_v2;
    const ugcComponents = $Customizer.$store.getters["ugc/getUGCEnabledComponents"];
    for (const component of ugcComponents) {
      const ugcObj = recipe?.components?.[component].ugc || recipe_v2.components?.[component].ugc;
      if(ugcObj) {
        if(!leftShoe) {
          await this.applyTexture({
            imgUrl:
              ugcObj.ugc_swatch
                ?.image_url,
            componentName: component,
          })
        } 
        else if(component === "vamp") this.flippedUGCTexture = await this.handleUGCLeftTextureVamp(); 
        else {
          const componentMesh = this.model3d.getObjectByName(component);
          this.flippedUGCTexture = await this.handleUGCLeftTextureQuarters();
          const componentPDM = this.pdm.components[component];
          const merchandisingUGC = componentPDM.ugc;
          const material_options = { ...merchandisingUGC.ugc_swatch.material_options };
          const loadedTextures = {
            map: this.flippedUGCTexture,
            normalMap: componentMesh.material.normalMap
          }
          // Create a new material since the material uuid is shared between the 3d Models.
          componentMesh.material = this.$model3D.sceneHelpers.createMaterial({
            mesh_type: material_options.type,
            options: Object.assign({}, material_options.mesh_options, loadedTextures),
            componentName:component,
            shadersType: componentPDM.ugc.shader_material
          });
        }
      }
    }
  }

  async handleLeftShoe(pdmView) {
    // Flip Component
    if(pdmView.components) {
      const components = pdmView.components && Object.keys(pdmView.components);
      components?.forEach((componentName) => {
        const pdmComponent = pdmView.components[componentName];
        const pos = pdmComponent.position;
        const rot = pdmComponent.rotation;
        const component = this.model3d.getObjectByName(componentName);
        if(component) {
          pdmComponent.flipHorizontal && component.scale.set(component.scale.x,-component.scale.y,component.scale.z);
          pos && component.position.set(pos.x,pos.y,pos.z);
          rot && component.rotation.set(rot.x,rot.y,rot.z);
        }
      })
    }
    await this.handleUGC(true);
  }

  // Solution for left UGC Texture in Quarters.
  async handleUGCLeftTextureQuarters() {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = 1024;
    canvas.height = 1024;

    // Fill background with white
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Load images asynchronously
    const loadImage = (src) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = src;
            img.onload = () => resolve(img);
            img.onerror = reject;
        });
    
    const ugcPlacements = this.$store.state.utils.pdm.components.quarters.ugc.placements;
    const image1Src = this.$store.state.recipe.recipe_v2.components.quarters.ugc.placements[1].image_url;
    const image2Src = this.$store.state.recipe.recipe_v2.components.quarters.ugc.placements[0].image_url;
    try {
        const img = await Promise.all([loadImage(image1Src), loadImage(image2Src)]);

        ugcPlacements.forEach((obj, index) => {
          const coordinates = obj.ugc_swatch_coordinates.medium["9.0"];
          ctx.save();
          ctx.scale(-1, 1); // Flip horizontally
          ctx.drawImage(img[index], -coordinates.x-coordinates.width, coordinates.y, coordinates.width, coordinates.height);
          ctx.restore();
        });
        // Create Three.js texture from canvas
        const texture = new THREE.CanvasTexture(canvas);
        texture.flipY = false;
        texture.repeat.set(28,28);
        texture.needsUpdate = true;
        return texture;
    } catch (error) {
        console.error("Error loading images:", error);
        return null;
    }
  }

  // Solution for left UGC Texture in Vamp
  handleUGCLeftTextureVamp() {
    const geometry = this.model3d.getObjectByName('vamp').geometry.clone();
    if (!geometry.attributes.uv3) return;

    const uvAttribute = geometry.attributes.uv3;
    for (let i = 0; i < uvAttribute.count; i++) {
        uvAttribute.setX(i, 1 - uvAttribute.getX(i)); // Flip U coordinate
    }
    uvAttribute.needsUpdate = true;
    this.model3d.getObjectByName('vamp').geometry = geometry;
  }

  async screenShot(snapshotSize,snapshotView,showSnapShot,floor) {
    const blob = await new Promise(resolve => this.renderer.domElement.toBlob(resolve, 'image/jpg')); 
    const recipeId = this.$store.state.recipe.recipe_v2.recipe_id;
    const size = snapshotSize;
    const view = snapshotView;
    const data = await this.$store.dispatch("utils/GET_PRODUCT_IMAGES_FROM_SCENE", { blob, recipeId, size, view});
    showSnapShot && this.$eventBus.$emit("snapshot-complete", data);
    return data;
  }

  disposeScene(){
    this.scene?.dispose();
    this.scene = null;
    this.animationFrameID = null;
    this.renderer?.forceContextLoss();
    this.renderer?.dispose();
    this.flippedUGCTexture?.dispose();
    this.renderer = null;
    this.camera = null;
    this.model3d = null;
    this.rootObj = null;
    this.flippedUGCTexture = null;
  }
};